import React from 'react';
import { arrayOf, bool, func, number, shape, string } from 'prop-types';
import clsx from 'clsx';
import { CustomPaging, PagingState } from '@devexpress/dx-react-grid';
import {
    Grid,
    Table,
    TableHeaderRow
} from '@devexpress/dx-react-grid-material-ui';
import moment from 'moment';

import CellRenderer from '../../components/RuleExceptionsTable/CellRenderer';
import MetaContext from '../../components/RuleExceptionsTable/MetaContext';
import NoDataCell from '../../components/RuleExceptionsTable/NoDataCell';

import defaultClasses from './WeatherDataTable.module.css';

const COLUMNS = [
    {
        getCellValue(row) {
            if (!row.timestamp) {
                return;
            }
            return moment.parseZone(row.timestamp).local().format('YYYY-MM-DD h:mm A');
        },
        name:  'timestamp',
        title: 'Timestamp'
    },
    {
        getCellValue(row) {
            if (!row.temp) {
                return;
            }
            return `${row.temp} (°F)`;
        },
        name:  'temp',
        title: 'Temperature'
    },
    {
        getCellValue(row) {
            if (!row.humidity) {
                return;
            }
            return `${row.humidity} (%)`;
        },
        name:  'humidity',
        title: 'Humidity '
    },
    {
        getCellValue(row) {
            if (!row.pressure) {
                return;
            }
            return `${row.pressure} (hPa)`;
        },
        name:  'pressure',
        title: 'Pressure '
    },
    {
        getCellValue(row) {
            if (!row.tempMin) {
                return;
            }
            return `${row.tempMin} (°F)`;
        },
        name:  'tempMin',
        title: 'Min Temp '
    },
    {
        getCellValue(row) {
            if (!row.tempMax) {
                return;
            }
            return `${row.tempMax} (°F)`;
        },
        name:  'tempMax',
        title: 'Max Temp '
    }
];

const StyledTable = (props) => (
    <Table.Table {...props} className={defaultClasses.table} />
);

const StyledHeaderCellComponent = (props) => (
    <TableHeaderRow.Cell
        {...props}
        className={clsx(defaultClasses.cell, defaultClasses.headerCell)}
    />
);

const TableColumnExtensions = [
    { columnName: 'timestamp', width: 175 },
    { columnName: 'temp', width: 150 },
    { columnName: 'humidity', width: 120 }
];

const WeatherDataTable = ({
    currentPage,
    error,
    weatherData,
    isLoading,
    onCurrentPageChange,
    onPageSizeChange,
    pageSize,
    totalCount
}) => {
    let noDataMessage = 'There is no Weather data for this time period';
    if (isLoading) {
        noDataMessage = 'Loading Weather Data...';
    } else if (error) {
        noDataMessage = `Error Loading Weather Data: ${error}`;
    }

    return (
        <MetaContext.Provider value={{ error }}>
            <Grid columns={COLUMNS} rows={weatherData}>
                <PagingState
                    currentPage={currentPage}
                    onCurrentPageChange={onCurrentPageChange}
                    onPageSizeChange={onPageSizeChange}
                    pageSize={pageSize}
                />
                <CustomPaging totalCount={totalCount} />
                <Table
                    cellComponent={CellRenderer}
                    columnExtensions={TableColumnExtensions}
                    messages={{
                        noData: noDataMessage
                    }}
                    noDataCellComponent={NoDataCell}
                    tableComponent={StyledTable}
                />
                <TableHeaderRow cellComponent={StyledHeaderCellComponent} />
            </Grid>
        </MetaContext.Provider>
    );
};

WeatherDataTable.defaultProps = {
    weatherData:         [],
    isLoading:           false,
    onCurrentPageChange: () => {},
    onPageSizeChange:    () => {}
};

WeatherDataTable.propTypes = {
    currentPage: number,
    error:       string,
    weatherData: arrayOf(shape({
        insertTimestamp: string,
        notes:           string,
        ruleName:        string
    })),
    isLoading:           bool,
    onCurrentPageChange: func,
    onPageSizeChange:    func,
    pageSize:            number,
    totalCount:          number
};

export default WeatherDataTable;
